/**
 * LUCY LIVEKIT VOICE MODULE
 * Real-time WebRTC voice for Lucy AI — WowStore.live
 * Uses LiveKit server + lucy-voice-agent for bi-directional voice
 *
 * Dependencies: livekit-client SDK (loaded from CDN)
 * Fallback: edge-tts + Web Speech API (existing system)
 */

(function() {
  'use strict';

  const LIVEKIT_TOKEN_URL = 'https://ai-api.wowstore.live/livekit/token';
  const LIVEKIT_SDK_URL = 'https://cdn.jsdelivr.net/npm/livekit-client@2.9.5/dist/livekit-client.umd.js';

  // State
  const state = {
    sdk: null,           // LiveKit SDK reference
    room: null,          // LiveKit Room instance
    connected: false,
    connecting: false,
    localTrack: null,    // Local microphone track
    remoteAudio: null,   // <audio> element for agent voice
    micEnabled: false,
    agentConnected: false,
    token: null,
    wsUrl: null,
    roomName: null,
    identity: null,
    sdkLoaded: false,
    sdkLoading: false,
    reconnectAttempts: 0,
    maxReconnectAttempts: 3
  };

  // ========== SDK LOADER ==========

  async function loadSDK() {
    if (state.sdkLoaded) return true;
    if (state.sdkLoading) {
      // Wait for ongoing load
      return new Promise((resolve) => {
        const check = setInterval(() => {
          if (state.sdkLoaded) { clearInterval(check); resolve(true); }
        }, 100);
        setTimeout(() => { clearInterval(check); resolve(false); }, 10000);
      });
    }

    state.sdkLoading = true;
    try {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = LIVEKIT_SDK_URL;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });

      if (window.LivekitClient) {
        state.sdk = window.LivekitClient;
        state.sdkLoaded = true;
        console.log('[Lucy LK] SDK loaded');
        return true;
      }
      throw new Error('SDK global not found');
    } catch (e) {
      console.warn('[Lucy LK] SDK load failed:', e);
      state.sdkLoading = false;
      return false;
    }
  }

  // ========== TOKEN ==========

  async function getToken() {
    const browserId = localStorage.getItem('lucy_browser_id') ||
      'b_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6);
    localStorage.setItem('lucy_browser_id', browserId);

    try {
      const resp = await fetch(LIVEKIT_TOKEN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ browser_id: browserId })
      });

      if (!resp.ok) throw new Error(`Token HTTP ${resp.status}`);

      const data = await resp.json();
      state.token = data.token;
      state.wsUrl = data.url;
      state.roomName = data.room;
      state.identity = data.identity;
      return data;
    } catch (e) {
      console.error('[Lucy LK] Token error:', e);
      return null;
    }
  }

  // ========== ROOM CONNECTION ==========

  async function connect() {
    if (state.connected || state.connecting) return state.connected;
    state.connecting = true;

    dispatch('lucy:livekit:status', { status: 'connecting' });

    // Load SDK
    if (!await loadSDK()) {
      state.connecting = false;
      dispatch('lucy:livekit:status', { status: 'sdk_failed' });
      return false;
    }

    // Get token
    const tokenData = await getToken();
    if (!tokenData) {
      state.connecting = false;
      dispatch('lucy:livekit:status', { status: 'token_failed' });
      return false;
    }

    try {
      const LK = state.sdk;

      // Create room
      state.room = new LK.Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Wire events before connecting
      wireRoomEvents(state.room);

      // Connect
      await state.room.connect(state.wsUrl, state.token, {
        autoSubscribe: true
      });

      state.connected = true;
      state.connecting = false;
      state.reconnectAttempts = 0;
      console.log('[Lucy LK] Connected to room:', state.roomName);
      dispatch('lucy:livekit:status', { status: 'connected', room: state.roomName });

      return true;
    } catch (e) {
      console.error('[Lucy LK] Connect error:', e);
      state.connecting = false;
      state.connected = false;
      dispatch('lucy:livekit:status', { status: 'connect_failed', error: e.message });
      return false;
    }
  }

  function wireRoomEvents(room) {
    const LK = state.sdk;

    // Agent joined
    room.on(LK.RoomEvent.ParticipantConnected, (participant) => {
      console.log('[Lucy LK] Participant joined:', participant.identity);
      if (participant.identity.startsWith('agent-')) {
        state.agentConnected = true;
        dispatch('lucy:livekit:agent', { connected: true, identity: participant.identity });
      }
    });

    // Agent left
    room.on(LK.RoomEvent.ParticipantDisconnected, (participant) => {
      if (participant.identity.startsWith('agent-')) {
        state.agentConnected = false;
        dispatch('lucy:livekit:agent', { connected: false });
      }
    });

    // Subscribe to agent audio tracks
    room.on(LK.RoomEvent.TrackSubscribed, (track, publication, participant) => {
      if (track.kind === LK.Track.Kind.Audio && participant.identity.startsWith('agent-')) {
        console.log('[Lucy LK] Agent audio subscribed');
        attachAgentAudio(track);
      }
    });

    // Agent audio unsubscribed
    room.on(LK.RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      if (track.kind === LK.Track.Kind.Audio && participant.identity.startsWith('agent-')) {
        detachAgentAudio();
      }
    });

    // Data channel messages (text responses from agent)
    room.on(LK.RoomEvent.DataReceived, (payload, participant) => {
      if (participant && participant.identity.startsWith('agent-')) {
        try {
          const text = new TextDecoder().decode(payload);
          const data = JSON.parse(text);
          console.log('[Lucy LK] Agent data:', data.type);
          dispatch('lucy:livekit:data', data);

          // If it's a text response, show in chat
          if (data.type === 'response' && data.text) {
            dispatch('lucy:livekit:response', { text: data.text });
          }
        } catch (e) {
          // Plain text message
          const text = new TextDecoder().decode(payload);
          dispatch('lucy:livekit:response', { text });
        }
      }
    });

    // Connection quality
    room.on(LK.RoomEvent.ConnectionQualityChanged, (quality, participant) => {
      if (participant.isLocal) {
        dispatch('lucy:livekit:quality', { quality });
      }
    });

    // Disconnected
    room.on(LK.RoomEvent.Disconnected, (reason) => {
      console.log('[Lucy LK] Disconnected:', reason);
      state.connected = false;
      state.agentConnected = false;
      state.micEnabled = false;
      dispatch('lucy:livekit:status', { status: 'disconnected', reason });

      // Auto-reconnect on unexpected disconnect
      if (reason !== 'CLIENT_INITIATED' && state.reconnectAttempts < state.maxReconnectAttempts) {
        state.reconnectAttempts++;
        console.log('[Lucy LK] Reconnecting... attempt', state.reconnectAttempts);
        setTimeout(() => connect(), 2000 * state.reconnectAttempts);
      }
    });

    // Reconnecting
    room.on(LK.RoomEvent.Reconnecting, () => {
      dispatch('lucy:livekit:status', { status: 'reconnecting' });
    });

    room.on(LK.RoomEvent.Reconnected, () => {
      state.connected = true;
      dispatch('lucy:livekit:status', { status: 'reconnected' });
    });
  }

  // ========== AUDIO ==========

  function attachAgentAudio(track) {
    if (!state.remoteAudio) {
      state.remoteAudio = document.createElement('audio');
      state.remoteAudio.id = 'lucyAgentAudio';
      state.remoteAudio.autoplay = true;
      state.remoteAudio.style.display = 'none';
      document.body.appendChild(state.remoteAudio);
    }
    track.attach(state.remoteAudio);
    dispatch('lucy:speaking', { isSpeaking: true });
  }

  function detachAgentAudio() {
    if (state.remoteAudio) {
      state.remoteAudio.srcObject = null;
    }
    dispatch('lucy:speaking', { isSpeaking: false });
  }

  // ========== MICROPHONE ==========

  async function enableMic() {
    if (!state.connected) {
      const ok = await connect();
      if (!ok) return false;
    }

    try {
      const LK = state.sdk;

      // Enable mic through LiveKit room
      await state.room.localParticipant.setMicrophoneEnabled(true);
      state.micEnabled = true;

      console.log('[Lucy LK] Mic enabled');
      dispatch('lucy:livekit:mic', { enabled: true });
      dispatch('lucy:listening', { isListening: true });
      return true;
    } catch (e) {
      console.error('[Lucy LK] Mic error:', e);
      dispatch('lucy:livekit:mic', { enabled: false, error: e.message });
      return false;
    }
  }

  async function disableMic() {
    if (!state.room) return;

    try {
      await state.room.localParticipant.setMicrophoneEnabled(false);
      state.micEnabled = false;
      dispatch('lucy:livekit:mic', { enabled: false });
      dispatch('lucy:listening', { isListening: false });
    } catch (e) {
      console.warn('[Lucy LK] Mic disable error:', e);
    }
  }

  async function toggleMic() {
    if (state.micEnabled) {
      await disableMic();
    } else {
      await enableMic();
    }
    return state.micEnabled;
  }

  // ========== TEXT MESSAGE VIA DATA CHANNEL ==========

  async function sendText(message) {
    if (!state.connected || !state.room) {
      console.warn('[Lucy LK] Not connected, cannot send text');
      return false;
    }

    try {
      const data = JSON.stringify({ type: 'text', text: message });
      const encoded = new TextEncoder().encode(data);
      await state.room.localParticipant.publishData(encoded, { reliable: true });
      console.log('[Lucy LK] Sent text:', message.substring(0, 50));
      return true;
    } catch (e) {
      console.warn('[Lucy LK] Send text error:', e);
      return false;
    }
  }

  // ========== DISCONNECT ==========

  async function disconnect() {
    if (state.room) {
      try {
        state.room.disconnect(true);
      } catch (e) {}
      state.room = null;
    }
    state.connected = false;
    state.connecting = false;
    state.micEnabled = false;
    state.agentConnected = false;
    detachAgentAudio();
    dispatch('lucy:livekit:status', { status: 'disconnected', reason: 'CLIENT_INITIATED' });
  }

  // ========== HELPERS ==========

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  function getState() {
    return {
      connected: state.connected,
      connecting: state.connecting,
      micEnabled: state.micEnabled,
      agentConnected: state.agentConnected,
      roomName: state.roomName,
      sdkLoaded: state.sdkLoaded
    };
  }

  // ========== PUBLIC API ==========

  window.LucyLiveKit = {
    connect,
    disconnect,
    enableMic,
    disableMic,
    toggleMic,
    sendText,
    getState,
    loadSDK,

    // Convenience: start voice session (connect + enable mic)
    async startVoice() {
      const connected = await connect();
      if (!connected) return false;
      return enableMic();
    },

    // Convenience: end voice session
    async endVoice() {
      await disableMic();
      await disconnect();
    },

    // Check if LiveKit is available
    isAvailable() {
      return state.connected && state.agentConnected;
    }
  };

  console.log('[Lucy LK] Module loaded');
})();
